// Atlas Assistant data layer (P4-CC12). The ONLY file that talks to Supabase for
// this feature — calls the `ai-assistant` Edge Function via
// supabase.functions.invoke, which attaches the caller's current-session JWT
// automatically (the function verifies it; it never sees the service_role key).
//
// Wire contract: every business-level outcome comes back as HTTP 200 with
// { ok: true, reply } or { ok: false, reason }. Only genuine protocol faults
// (missing/invalid auth, malformed body, wrong method, a server/config error)
// use a real non-2xx HTTP status — see supabase/functions/ai-assistant/index.ts.
//
// Client-side caps mirror the server's own clamp EXACTLY (same constants,
// same trim-then-drop-oldest logic). Both layers exist deliberately: this one
// keeps the normal path from ever tripping the server's clamp; the server's is
// the real control, since the client cannot be trusted.
import { supabase } from "./supabaseClient";

export const MAX_MESSAGE_CHARS = 2000;
export const MAX_HISTORY_TURNS = 20;
export const MAX_HISTORY_CHARS = 20000; // total across all history entries

const REASON_MESSAGES = {
  auth: "Please sign in again to use the assistant.",
  bad_request: "That message couldn't be sent — try rephrasing it.",
  method: "Something went wrong reaching Atlas. Please try again.",
  error: "Atlas is temporarily unavailable. Please try again shortly.",
  quota: "Atlas has reached its daily message limit for your company. Please try again tomorrow.",
  busy: "Atlas is busy right now — please try again in a moment.",
  declined: "Atlas couldn't answer that one — try rephrasing your question.",
};

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

function toError(reason) {
  const e = new Error(REASON_MESSAGES[reason] || "Could not reach Atlas. Please try again.");
  e.code = reason;
  return e;
}

// Same clamp the server applies (see index.ts's identical constants/logic):
// keep only well-shaped entries, keep the last MAX_HISTORY_TURNS, truncate each
// entry's content, then drop from the oldest end until the total fits.
function clampHistory(history) {
  const clean = (Array.isArray(history) ? history : [])
    .filter((h) => h && (h.role === "user" || h.role === "assistant") && typeof h.content === "string")
    .slice(-MAX_HISTORY_TURNS)
    .map((h) => ({ role: h.role, content: h.content.slice(0, MAX_MESSAGE_CHARS) }));
  let total = clean.reduce((sum, h) => sum + h.content.length, 0);
  while (total > MAX_HISTORY_CHARS && clean.length) {
    total -= clean.shift().content.length;
  }
  return clean;
}

/**
 * Send a message to Atlas. Returns the assistant's reply text; throws a
 * friendly, code-tagged Error (e.code = the reason) on any failure.
 * @param {string} message
 * @param {Array<{role:'user'|'assistant', content:string}>} [history]
 * @returns {Promise<string>}
 */
export async function sendAssistantMessage(message, history = []) {
  const trimmed = String(message || "").trim().slice(0, MAX_MESSAGE_CHARS);
  if (!trimmed) throw new Error("Enter a message first.");
  const { data, error } = await client().functions.invoke("ai-assistant", {
    body: { message: trimmed, history: clampHistory(history) },
  });
  if (error) {
    // Protocol-fault path (401/400/405/500) — the function still sends an
    // {ok,reason} body even on a non-2xx status; try to recover it for a
    // precise message, falling back to a generic one (e.g. a true network
    // failure has no readable body at all).
    let reason;
    try { reason = (await error.context?.json())?.reason; } catch { /* no body to read */ }
    throw toError(reason || "error");
  }
  if (!data || data.ok !== true) throw toError(data?.reason);
  // Defence in depth: the Edge Function itself now guards against ever
  // sending {ok:true, reply:""} (an empty successful reply becomes
  // {ok:false, reason:"busy"} server-side) — but never trust the wire for
  // this either. An unexpectedly empty reply is treated the same way here,
  // so the panel can never render a blank chat bubble that looks like the
  // app is broken.
  if (!data.reply || !String(data.reply).trim()) throw toError("busy");
  return data.reply;
}
